<template>
  <div class="analytics article-analytics mb-3">
    <data-warning
      :disabled="isDisabled"
      :incomplete="isIncomplete"
      :type="analyticsType"
      :published-recently="publishedPast2hours"
    />

    <filter-bar
      v-if="!isDisabled"
      :timeframe="timeframe"
      :switch-timeframe="false"
      @change="() => null"
    />

    <stats
      :data="basicMetrics"
      :props="basicMetricsShow"
      :min-width="minWidth"
      :disabled="isDisabled"
    />
    <funnel-chart
      v-if="dimensions"
      :width="dimensions.width"
      :height="500"
      :client-width="clientWidth"
      :min-width="minWidth + 250"
      :items="funnelItems"
      :processed="funnelProcessed"
      :type="analyticsType"
      :labels-long="[
        'Emails Delivered',
        'Unique Email Opens',
        'Unique Email Clicks',
        'Web views',
      ]"
      :labels-short="['DELI', 'OPEN', 'VIEW', 'DOWN', 'DOWN']"
      :disabled="isDisabled"
    />

    <bar-chart
      v-if="dimensions"
      :width="dimensions.width"
      :height="500"
      :client-width="clientWidth"
      :min-width="minWidth"
      :items="barChartItems"
      :type="analyticsType"
      :disabled="isDisabled"
    />

    <grouped-line-chart
      v-if="timeframeLabels && dimensions && !isDisabled"
      :items="readership"
      :item-groups="lineChartGroups"
      :width="dimensions.width"
      :client-width="clientWidth"
      :min-width="minWidth"
      :height="400"
      :timeframe-labels="timeframeLabels"
      :type="analyticsType"
      :disabled="isDisabled"
    />

    <multiple-ranking-lists
      v-if="!isDisabled"
      :settings="topListTypes"
      :ranking-lists="topLists"
      :client-width="clientWidth"
      :data="data"
    />
  </div>
</template>

<script>
import { getTimeframePublishedReadership } from '@/analytics/utils/keen-utils'
import {
  constructFunnelItems,
  constructBarChartItems,
  constructReadershipGrouped,
} from '@/analytics/data-analyser'

import AnalyticsMixin from '@/analytics/mixins/AnalyticsMixin'

import DataWarning from '@/components/DataWarning'
import FilterBar from '@/components/FilterBar'
import Stats from '@/components/Stats'
import BarChart from '@/components/d3/BarChart'
import FunnelChart from '@/components/d3/FunnelChart'
import GroupedLineChart from '@/components/d3/GroupedLineChart'

export default {
  components: {
    DataWarning,
    FilterBar,
    Stats,
    FunnelChart,
    BarChart,
    GroupedLineChart,
  },
  mixins: [AnalyticsMixin],
  async mounted() {
    const response = await this.loadData(
      `articles/${this.data.id}`,
      undefined,
      this.$login.channelScopeId()
    )

    this.basicMetrics = {
      current: {
        webViews: response.data.totals.webViews,
        emailDelivers: response.data.totals.emailDelivers,
        emailClicks: response.data.totals.emailClicks,
        emailOpens: response.data.totals.emailOpens,
      },
    }

    this.readership = [
      {
        key: 'webViews',
        name: 'Web Views',
        val: {
          article: response.data.series.webViewsDaily.map((x) => x.value),
          // channel: response.data.webViewAveragesChart.map(
          //   (x) => x.channelAverage
          // ),
          // network: response.data.webViewAveragesChart.map(
          //   (x) => x.networkAverage
          // ),
        },
      },
      {
        key: 'emailOpens',
        name: 'Email Opens',
        val: {
          article: response.data.series.emailOpensDaily.map((x) => x.value),
          // channel: response.data.emailOpenAveragesChart.map(
          //   (x) => x.channelAverage
          // ),
          // network: response.data.emailOpenAveragesChart.map(
          //   (x) => x.networkAverage
          // ),
        },
      },
      {
        key: 'emailClicks',
        name: 'Email Clicks',
        val: {
          article: response.data.series.clicksDaily.map((x) => x.value),
          // channel: response.data.emailOpenAveragesChart.map(
          //   (x) => x.channelAverage
          // ),
          // network: response.data.emailOpenAveragesChart.map(
          //   (x) => x.networkAverage
          // ),
        },
      },
    ]

    /*
    {
    "id": "74177",
    "name": "Téo COHELEACH",
    "value": 4,
    "lastValue": 0,
    "diffNum": false,
    "diffStr": "",
    "webDownloads": 0,
    "emailViews": 0,
    "sendgridOpened": 0,
    "valueRank": 1,
    "lastValueRank": -1,
    "change": 0,
    "engagement": 4,
    "position": 1,
    "account": "Global Sovereign Advisory",
    "account_id": 4399,
    "channel": "-",
    "channel_id": "-",
    "analyst": "-",
    "analyst_id": null,
    "companies": null,
    "email": "tcoheleach@globalsov.com"
}
    */
    this.topLists = {
      articleUser: response.data.topReaders.map((tr) => ({
        id: tr.user.id,
        name: tr.user.name,
        value: tr.webViews,
        lastValue: 0,
        diffNum: false,
        diffStr: '',
        webDownloads: tr.downloads || 0,
        emailViews: 0,
        sendgridOpened: 0,
        valueRank: 1,
        lastValueRank: -1,
        change: 0,
        engagement: 4,
        position: 1,
        account: tr.user.account,
        account_id: tr.user.account_id,
        channel: '-',
        channel_id: '-',
        analyst: '-',
        analyst_id: null,
        companies: null,
        email: '',
      })),
      channelArticle: (response.data.channelLeague || []).map((tr) => ({
        id: tr.article.id,
        name: tr.article.title,
        value: tr.webViews,
        lastValue: 0,
        diffNum: false,
        diffStr: '',
        webDownloads: tr.downloads || 0,
        emailViews: 0,
        sendgridOpened: 0,
        valueRank: tr.rank,
        lastValueRank: tr.rank,
        change: 0,
        engagement: 4,
        position: tr.rank,
        account: '-',
        account_id: '-',
        channel: '-',
        channel_id: '-',
        analyst: '-',
        analyst_id: null,
        companies: null,
        email: '',
      })),
      networkArticle: (response.data.networkLeague || []).map((tr) => ({
        id: tr.article.id,
        name: tr.article.title,
        value: tr.webViews,
        lastValue: 0,
        diffNum: false,
        diffStr: '',
        webDownloads: tr.downloads || 0,
        emailViews: 0,
        sendgridOpened: 0,
        valueRank: tr.rank,
        lastValueRank: tr.rank,
        change: 0,
        engagement: 4,
        position: tr.rank,
        account: '-',
        account_id: '-',
        channel: '-',
        channel_id: '-',
        analyst: '-',
        analyst_id: null,
        companies: null,
        email: '',
      })),
    }
  },
  data() {
    return {
      analyticsType: 'article',
      lineChartGroups: ['webViews', 'emailOpens'],

      basicMetricsShow: [
        'emailDelivers',
        'emailOpens',
        'emailClicks',
        'webViews',
      ],
      // basicMetricProps: ['sendgridProcessed', 'sendgridOpened',   'webViews', 'webViewsChannel', 'webViewsNetwork', 'webViewsUnique', 'webViewsUniqueChannel', 'webDownloads', 'webDownloadsChannel', 'webDownloadsNetwork', 'scrollDepth', 'engagedMinutes', 'sendgridProcessedUnique', 'sendgridDeliveredUnique', 'sendgridOpenedUnique', 'sendgridDelivered', 'sendgridDeliveredUnique'],

      basicMetricProps: [
        'emailDelivers',
        'emailOpens',
        'emailClicks',
        'webViews',
      ],
      timeframe: 'this_5_years',
      basicTopListTypes: {
        articleUser: {
          title: 'Top Readers',
          path: 'users',
          colWidth: 12,
          activeEnabled: false,
          cols: [
            'name',
            'account',
            'webDownloads',
            'sendgridOpened',
            'engagement',
          ],
          type: 'infinity-rank',
        },
        channelArticle: {
          title: 'Channel League Table',
          path: 'articles',
          colWidth: 12,
          activeEnabled: true,
          cols: ['name', 'webDownloads', 'sendgridOpened', 'engagement'],
          type: 'rank',
        },
        networkArticle: {
          title: 'Network League Table',
          path: 'articles',
          colWidth: 12,
          activeEnabled: true,
          cols: [
            'name',
            'channel',
            'webDownloads',
            'sendgridOpened',
            'engagement',
          ],
          type: 'rank',
        },
      },
      enableWarning: true,
    }
  },
  computed: {
    sources() {
      return {
        web: ['article_views', 'article_downloads'],
        sendgrid: ['email_processed', 'email_delivered', 'email_open'],
      }
    },
    isReady() {
      const ready = this.basicMetrics && this.basicMetrics.current
      console.log({ ready })
      return ready
    },
    topListTypes() {
      const results = { ...this.basicTopListTypes }
      for (const type in results) {
        if (type.includes('network') && !this.$login.can('analytics.network')) {
          delete results[type]
        }
      }

      return results
    },

    lineChartSources() {
      return { web: ['article_views'] }
    },

    timeframePublished() {
      return 'this_7_days'
      // return this.data
      //   ? getTimeframePublishedReadership(this.data)
      //   : this.timeframe
    },

    timeframeLabels() {
      if (!this.cogData) {
        return []
      }

      const labels = this.cogData.series.webViewsDaily.map((item) => item.date)
      return labels
    },

    items() {
      const items = this.cogData
      if (!items) {
        return {
          key: 'webViews',
          val: [],
          name: 'Web views',
        }
      }

      return { web: items.cogData.series.webViewsDaily.map((d) => d.value) }
    },
  },
  watch: {

        timeframe: function (timeframe, oldData) {
      // this.loadData(timeframe)
      if (this.cogData) {
        this.treemapItems = this.cogData.engagement[
          this.timeframe
        ].accounts.map((ae) => ({
          id: ae.id.toString(),
          name: ae.name,
          value: ae.value,
          lastValue: ae.lastValue,
          diffNum: ae.difference,
          diffStr: ae.differenceText,
          valueRank: ae.rank,
          lastValueRank: ae.prevRank,
          change: ae.rankChange,
          position: ae.rank,
        }))
        this.basicMetrics = this.cogData.basicMetrics[this.timeframe].totals
      }
    },

    cogData() {
        this.treemapItems = this.cogData.engagement[
          this.timeframe
        ].accounts.map((ae) => ({
          id: ae.id.toString(),
          name: ae.name,
          value: ae.value,
          lastValue: ae.lastValue,
          diffNum: ae.difference,
          diffStr: ae.differenceText,
          valueRank: ae.rank,
          lastValueRank: ae.prevRank,
          change: ae.rankChange,
          position: ae.rank,
        }))
        this.basicMetrics = this.cogData.basicMetrics[this.timeframe].totals

    },

    basicMetrics(data) {
      if (data) {

        this.funnelItems = constructFunnelItems(
          this.basicMetrics.current,
          this.analyticsType
        )
        this.funnelProcessed = {
          primary: this.basicMetrics.current.emailDelivers,
        }
        this.barChartItems = constructBarChartItems(
          this.basicMetrics.current,
          ['emailOpens', 'webViews', 'emailClicks'],
          this.analyticsType
        )

        console.log('rs', this.readership)
        console.log('fi', this.funnelItems)
        console.log('fp', this.funnelProcessed)
      }
    },
    keenAnalyses: function (newData, oldData) {
      if (newData !== null) {
        /*        this.constructBasicMetrics()

        this.funnelItems = constructFunnelItems(this.basicMetrics.current, this.analyticsType)

        // Feature: Update for addition channel & network data
        this.funnelProcessed = {
          primary: this.basicMetrics.current.sendgridProcessedUnique,
        }
        console.log('basic metrics',this.funnelProcessed)


        console.log(newData)

*/
        // this.readership = constructReadershipGrouped(newData['readership'], this.lineChartSources, this.analyticsType, ['channel', 'network'])
        // console.log('readership',this.readership)
        // const customSources = { web: ['article_views', 'article_downloads'], sendgrid: ['email_open']}
        // this.constructTopList('article', 'user', 'custom_attributes.user_id', this.data.id, customSources, 'infinity-rank')
        // this.constructTopList('channel', 'article', 'custom_attributes.alertable_id', this.data.channel_content_type.channel.id, this.sources, 'rank', this.data.id, 10, this.basicMetrics)
        // if (this.$login.can('analytics.network')) {
        //   this.constructTopList('network', 'article', 'custom_attributes.alertable_id', 0, this.sources, 'rank', this.data.id, 10, this.basicMetrics)
        // }
        // console.log('top lists ???? ',this.topLists)
        /*
         */
      }
    },
  },
  methods: {
    async fetchStreamAnalyses() {
      const analyses = await this.$keen.fetchStreamAnalyses(
        this.analyticsType,
        this.sources,
        this.timeframe,
        this.data.id,
        this.data.channel_content_type.channel.id
      )

      analyses['readership'] = await this.$keen.fetchArticleReadership(
        this.data
      )

      this.keenAnalyses = analyses
    },
  },
}
</script>
